import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
    ContactLogDocument,
    DeleteClientContactDocument,
    OutreachQueueDocument,
    UpdateClientContactDocument,
} from "@/graphql/clients.generated";
import type { ContactLogDialogEntry } from "@/pages/Alumni/components/ContactLogDialog";
import type { LoggedContact } from "@/pages/Alumni/components/LogContactForm";
import {
    API_OUTCOME_FOR_OUTCOME,
    CONTACT_TYPE_FOR_CHANNEL,
    fullName,
    toCalendarDate,
} from "@/pages/Alumni/helpers/client";

/**
 * Correct or delete a logged contact.
 *
 * A change moves more than the contact itself — the client's next due date is counted from their
 * contacts — so, as with logging one, everything it touches is re-read rather than patched: the
 * contact log and the queue are refetched, and the client's cached history is dropped so their
 * profile reads it fresh.
 *
 * Each rejects when the change could not be made, after saying so, so the dialog keeps its place.
 */
export const useContactChanges = () => {
    const { t } = useTranslation();
    const refetch = {
        refetchQueries: [
            ContactLogDocument,
            OutreachQueueDocument,
        ],
        awaitRefetchQueries: true,
    };
    const [updateClientContact] = useMutation(UpdateClientContactDocument, refetch);
    const [deleteClientContact] = useMutation(DeleteClientContactDocument, refetch);

    const forgetHistory = (cache: ApolloCache, clientId: string) => {
        cache.evict({ id: cache.identify({ __typename: "Client", id: clientId }), fieldName: "contacts" });
    };

    const saveContact = async ({ contact, client }: ContactLogDialogEntry, edited: LoggedContact): Promise<void> => {
        const name = fullName(client);
        try {
            await updateClientContact({
                variables: {
                    contactId: contact.id,
                    input: {
                        type: CONTACT_TYPE_FOR_CHANNEL[edited.channel],
                        outcome: API_OUTCOME_FOR_OUTCOME[edited.outcome],
                        notes: edited.note ?? null,
                        followUpDate: edited.followUpOn ? toCalendarDate(edited.followUpOn) : null,
                    },
                },
                update: (cache) => forgetHistory(cache, client.id),
            });
        } catch (error) {
            toast.error(t("admin.alumni.contactLog.detail.saveFailed", { name }));
            throw error;
        }
        toast.success(t("admin.alumni.contactLog.detail.saved", { name }));
    };

    const deleteContact = async ({ contact, client }: ContactLogDialogEntry): Promise<void> => {
        const name = fullName(client);
        try {
            await deleteClientContact({
                variables: { contactId: contact.id },
                update: (cache) => {
                    cache.evict({ id: cache.identify({ __typename: "ClientContact", id: contact.id }) });
                    forgetHistory(cache, client.id);
                },
            });
        } catch (error) {
            toast.error(t("admin.alumni.contactLog.detail.deleteFailed", { name }));
            throw error;
        }
        toast.success(t("admin.alumni.contactLog.detail.deleted", { name }));
    };

    return { saveContact, deleteContact };
};
